<template>
  <div v-if="file">
    <h1>Email documents</h1>
    <FileWorking :file="file" />
    <v-alert :value="true" type="success"
      >Documents have already been saved to the file. Use this page to send the
      documents by email. Or click the link above to return to the
      file.</v-alert
    >
    <v-card>
      <v-card-text>
        <v-form ref="formEmailDocuments" v-model="valid" lazy-validation>
          <h2>To</h2>
          <v-checkbox
            v-model="emailParameters.emailAddressItems"
            :value="emailAddressItem"
            v-for="(emailAddressItem, emailAddressIndex) in emailAddressItems"
            :key="`emailAddress${emailAddressIndex}`"
            :rules="rulesEmailAddressItems"
          >
            <template slot="label">
              <span v-if="emailAddressItem.emailAddress"
                >{{ emailAddressItem.emailAddress.personal }} &lt;{{
                  emailAddressItem.emailAddress.address
                }}&gt;</span
              >
              <span v-if="!emailAddressItem.emailAddress">{{
                emailAddressItem.entity.id
              }}</span>
            </template>
          </v-checkbox>
          <v-text-field
            label="From"
            :value="currentEmailFormatted"
            outline
            readonly
          />
          <EmailTemplates
            :tag="'document_email'"
            :fileid="$route.params.objectId"
            :entities="$route.params.entities"
            @updated="updated"
          />
          <EmailSignature :fileId="$route.params.objectId" />
          <div v-if="documents && documents.length">
            <h2>Documents</h2>
            <v-checkbox
              v-model="emailParameters.documents"
              :value="document.id"
              v-for="document in modifiedDocuments"
              :key="document.id"
              :rules="rulesAttachments"
            >
              <template slot="label">
                <span v-if="document.template"
                  >{{ document.template.code }} -
                  {{ document.template.name }}</span
                >
                <span v-if="!document.template">{{ document.id }}</span>
              </template>
            </v-checkbox>
          </div>
          <div v-if="uploadFiles && uploadFiles.length">
            <h2>Common uploads</h2>
            <v-checkbox
              v-model="emailParameters.uploadFiles"
              :value="uploadFile.id"
              v-for="uploadFile in uploadFiles"
              :key="uploadFile.id"
              :rules="rulesAttachments"
            >
              <template slot="label">
                <span v-if="uploadFile.uploadDescription">{{
                  uploadFile.uploadDescription.body
                }}</span>
                <span v-if="!uploadFile.uploadDescription">{{
                  uploadFile.id
                }}</span>
              </template>
            </v-checkbox>
          </div>
          <hr />
          <v-checkbox
            v-model="emailParameters.copy"
            :label="`Send a carbon copy of the email to ${currentEmailAddress.address}`"
            v-if="currentEmailAddress"
          ></v-checkbox>
          <v-btn
            :disabled="sendingEmails"
            :loading="sendingEmails"
            @click="send"
            color="primary"
          >
            <v-icon>send</v-icon>&#160;Send email and continue
          </v-btn>
          <v-btn @click="skip">
            <v-icon>print</v-icon>&#160;Skip emailing and go to printing
          </v-btn>
        </v-form>
        <v-dialog v-model="sendingEmails" persistent width="300">
          <v-card color="error" dark>
            <v-card-text>
              <span>Waiting for server to send emails...</span>
              <v-progress-linear
                indeterminate
                color="white"
                class="mb-0"
              ></v-progress-linear>
            </v-card-text>
          </v-card>
        </v-dialog>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import EmailSignature from "@/components/layout/widgets/EmailSignature";
import EmailTemplates from "@/components/layout/widgets/EmailTemplates";
import FileWorking from "@/components/link/FileWorking";
import nbmlaw from "@/api/nbmlaw";
export default {
  name: "DocumentEmail",
  metaInfo() {
    return {
      title: "Document email",
      titleTemplate: "%s - Document - Conveyancing Tracker",
      htmlAttrs: {
        lang: "en",
        amp: true
      }
    };
  },
  components: {
    EmailSignature,
    EmailTemplates,
    FileWorking
  },
  computed: {
    modifiedDocuments() {
      if (!this.documents) {
        return undefined;
      }
      return this.documents.map((d) => {
        const template =
          this.templates && this.templates[d.id]
            ? this.templates[d.id]
            : undefined;
        return { ...d, template };
      });
    },
    currentEmailFormatted() {
      if (!this.currentEmailAddress) {
        return undefined;
      }
      return `${this.currentEmailAddress.personal} <${this.currentEmailAddress.address}>`;
    },
    rulesEmailAddressItems() {
      return [
        this.emailParameters.emailAddressItems.length > 0 ||
          "At least one email address should be selected"
      ];
    },
    rulesAttachments() {
      const sum =
        this.emailParameters.documents.length +
        this.emailParameters.uploadFiles.length;
      return [sum > 0 || "At least one attachment should be selected"];
    }
  },
  data() {
    return {
      entities: null,
      emailAddressItems: null,
      file: null,
      currentEmailAddress: null,
      documents: null,
      templates: null,
      uploadFiles: null,
      sendingEmails: false,
      emailParameters: {
        emailAddressItems: [],
        documents: [],
        uploadFiles: [],
        subject: null,
        body: null,
        copy: true
      },
      valid: false
    };
  },
  methods: {
    async fetchFile() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.$route.params.objectId}`
      );
      // console.log(response.data);
      this.file = response.data;
    },
    async fetchCurrentUserEmail() {
      const response = await nbmlaw.get(
        `/cors/admin/file/files/${this.$route.params.objectId}/emailAddress`
      );
      // console.log(response.data);
      this.currentEmailAddress = response.data;
    },
    async fetchEntitiesEmailAddressItems() {
      const response = await nbmlaw.get(
        `/cors/admin/directory/entities/multiple/${this.$route.params.entities}/emailAddresses`
      );
      // console.log(response.data);
      this.emailAddressItems = response.data;
    },
    async fetchDocuments() {
      if (this.$route.query.documents && this.$route.query.documents.length) {
        const response = await nbmlaw.get(
          `/cors/admin/document/documents/multiple/${this.$route.query.documents}`
        );
        // console.log(response.data);
        this.documents = response.data;
      }
    },
    async fetchTemplatesByDocument() {
      if (this.$route.query.documents && this.$route.query.documents.length) {
        const response = await nbmlaw.get(
          `/cors/admin/document/documents/multiple/${this.$route.query.documents}/templates`
        );
        // console.log(response.data);
        this.templates = response.data;
      }
    },
    async fetchUploadFiles() {
      const response = await nbmlaw.get(
        `/cors/admin/document/uploadFiles/multiple/${this.$route.query.uploads}`
      );
      // console.log(response.data);
      this.uploadFiles = response.data;
    },
    async send() {
      if (this.$refs.formEmailDocuments.validate()) {
        this.sendingEmails = true;
        try {
          await nbmlaw.post(
            `/cors/admin/file/files/${this.$route.params.objectId}/emailDocuments`,
            this.emailParameters
          );
          // console.log(response.data);
          this.skip();
        } catch (err) {
          this.sendingEmails = false;
        }
      }
    },
    skip() {
      this.$router.push({
        name: "documentPrint",
        params: {
          objectType: this.$route.params.objectType,
          objectId: this.$route.params.objectId,
          entityTypeId: this.$route.params.entityTypeId,
          entities: this.$route.params.entities
        },
        query: {
          documents: this.$route.query.documents,
          uploads: this.$route.query.uploads
        }
      });
    },
    updated(e) {
      if (e) {
        // console.log(e);
        this.emailParameters = {
          ...this.emailParameters,
          subject: e.subject,
          body: e.body
        };
        // console.log(this.emailParameters);
      }
    }
  },
  watch: {
    emailAddressItems: {
      handler() {
        if (!this.emailAddressItems.length) {
          this.skip();
        } else {
          this.emailParameters.emailAddressItems = this.emailAddressItems.map(
            (a) => a
          );
        }
      }
    },
    uploadFiles: {
      handler() {
        if (this.uploadFiles && this.uploadFiles.length) {
          this.emailParameters.uploadFiles = this.uploadFiles.map((u) => u.id);
        }
      }
    },
    documents: {
      handler() {
        if (this.documents && this.documents.length) {
          this.emailParameters.documents = this.documents.map((d) => d.id);
        }
      }
    }
  },
  created() {
    this.fetchCurrentUserEmail();
    this.fetchFile();
    this.fetchEntitiesEmailAddressItems();
    this.fetchDocuments();
    this.fetchTemplatesByDocument();
    if (this.$route.query.uploads && this.$route.query.uploads.length) {
      this.fetchUploadFiles();
    }
  }
};
</script>
